import { useTenantMemberships } from "@/contexts/TenantContext";
import { LazoPaths } from "@/enums/LazoPaths";
import ApiResponseV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/ApiResponseV2";
import { LazoChatConversation, LazoChatConversationShell, mapToShell } from "@/models/ai-chat/LazoChatConversation";
import { LazoChatMessage } from "@/models/ai-chat/LazoChatMessage";
import { SmallConversationMember } from "@/models/ai-chat/SmallConversationMember";
import LazoChatConversationService, { ChatActivityFilter, ChatDateFilter } from "@/services/ai-chat/LazoChatConversationService";
import LazoChatMessageService from "@/services/ai-chat/LazoChatMessageService";
import { Dispatch, SetStateAction, createContext, useCallback, useContext, useEffect, useState } from "react";
import { generatePath, useNavigate } from "react-router-dom";
import { PageState } from "../portal/PortalChatContext";

export type PageFetchFn = (resetPage?: PageState) => void;
export type FilteredPageFetchFn = (filters?: any, resetPage?: PageState) => void;

type Props = {
  chats: LazoChatConversationShell[];
  setChats: Dispatch<SetStateAction<LazoChatConversationShell[]>>;
  selectedChat: LazoChatConversationShell | null;
  setSelectedChat: Dispatch<SetStateAction<LazoChatConversationShell | null>>;
  messages: LazoChatMessage[];
  setMessages: Dispatch<SetStateAction<LazoChatMessage[]>>;
  chatsSearchValue: string | null;
  setChatsSearchValue: Dispatch<SetStateAction<string | null>>;
  chatsFirstLoad: boolean;
  setChatsFirstLoad: Dispatch<SetStateAction<boolean>>;
  members: SmallConversationMember[];
  setMembers: Dispatch<SetStateAction<SmallConversationMember[]>>;
  chatsLoadingPage: boolean;
  fetchChatsPage: FilteredPageFetchFn;
  messagesFirstLoad: boolean;
  messagesLoadingPage: boolean;
  fetchMessagesPage: PageFetchFn;
  selectedActivityFilter: ChatActivityFilter;
  setSelectedActivityFilter: Dispatch<SetStateAction<ChatActivityFilter>>;
  selectedDateFilter: ChatDateFilter;
  setSelectedDateFilter: Dispatch<SetStateAction<ChatDateFilter>>;
  chatPreview: LazoChatConversation;
  setChatPreview: Dispatch<SetStateAction<LazoChatConversation>>;
  handleChatSelect: (chat: LazoChatConversationShell) => void;
  newConversationMembers: string[];
  setNewConversationMembers: Dispatch<SetStateAction<string[]>>;
  startNewChat: (message: string) => void;
  createPrivateGroupChat: (message: string, members: string[]) => Promise<ApiResponseV2<LazoChatConversation>>;
  showMobileChatView: boolean;
  setShowMobileChatView: Dispatch<SetStateAction<boolean>>;
};

const ctx = createContext<Props>(null);

export function OneChatProvider({ children }) {
  const [chatsPageState, setChatsPageState] = useState<PageState>({ page: -1, totalPages: null });
  const [chats, setChats] = useState<LazoChatConversationShell[]>([]);
  const [selectedChat, setSelectedChat] = useState<LazoChatConversationShell | null>(null);

  const [members, setMembers] = useState<SmallConversationMember[]>([]);

  const [chatsSearchValue, setChatsSearchValue] = useState<string | null>(null);
  const [chatsFirstLoad, setChatsFirstLoad] = useState<boolean>(true);
  const [chatsLoadingPage, setChatsLoadingPage] = useState<boolean>(false);

  const [messages, setMessages] = useState<LazoChatMessage[]>([]);
  const [messagesPageState, setMessagesPageState] = useState<PageState>({ page: -1, totalPages: null });
  const [messagesFirstLoad, setMessagesFirstLoad] = useState<boolean>(true);
  const [messagesLoadingPage, setMessagesLoadingPage] = useState<boolean>(false);

  const [selectedActivityFilter, setSelectedActivityFilter] = useState<ChatActivityFilter>(ChatActivityFilter.ALL);
  const [selectedDateFilter, setSelectedDateFilter] = useState<ChatDateFilter | null>(null);

  const [chatPreview, setChatPreview] = useState<LazoChatConversation | null>(null);

  const [newConversationMembers, setNewConversationMembers] = useState<string[]>([]);

  const [showMobileChatView, setShowMobileChatView] = useState<boolean>(false);

  const { currentTenantMembership } = useTenantMemberships();
  const navigate = useNavigate();

  const fetchChatsPage: FilteredPageFetchFn = useCallback(
    (filters?: any, resetPage?: PageState) => {
      const pageToFetch = resetPage ? resetPage.page : chatsPageState.page + 1;
      // Check it has more pages or is not a reset
      if (!resetPage && chatsPageState.totalPages !== null && pageToFetch >= chatsPageState.totalPages) {
        return;
      }

      resetPage && setChatsFirstLoad(true);
      !resetPage && setChatsLoadingPage(true);

      LazoChatConversationService.getPrivateConversations(currentTenantMembership.tenant.externalId, pageToFetch, filters)
        .then((r) => {
          r.onSuccess((p) => {
            setChatsPageState({ page: p.page.number, totalPages: p.page.totalPages });
            if (p.page.number === 0) {
              setChats(p.content);
            } else {
              setChats((prev) => prev.concat(p.content));
            }
          });
          r.notifyErrorIfPresent();
        })
        .finally(() => {
          setChatsLoadingPage(false);
          setChatsFirstLoad(false);
        });
    },
    [chatsPageState.page]
  );

  useEffect(() => {
    fetchChatsPage(
      {
        searchValue: chatsSearchValue,
        activityFilter: selectedActivityFilter,
        dateFilter: selectedDateFilter,
      },
      { page: 0, totalPages: null }
    );
  }, [currentTenantMembership.tenant.externalId, chatsSearchValue, selectedDateFilter, selectedActivityFilter]);

  const fetchMessagesPage = useCallback(
    (resetPage?: PageState) => {
      if (!selectedChat) {
        setMessagesLoadingPage(false);
        setMessagesFirstLoad(false);
        return;
      }
      const pageToFetch = resetPage ? resetPage.page : messagesPageState.page + 1;
      // Check it has more pages or is not a reset
      if (!resetPage && messagesPageState.totalPages !== null && pageToFetch >= messagesPageState.totalPages) {
        return;
      }

      resetPage && setMessagesFirstLoad(true);
      !resetPage && setMessagesLoadingPage(true);

      LazoChatMessageService.findByConversationExternalIdPaged(selectedChat.externalId, pageToFetch)
        .then((r) => {
          r.onSuccess((p) => {
            setMessagesPageState({ page: p.page.number, totalPages: p.page.totalPages });
            if (p.page.number === 0) {
              setMessages(p.content);
            } else {
              setMessages((prev) => [...p.content, ...prev]);
            }
          });
          r.notifyErrorIfPresent();
        })
        .finally(() => {
          setMessagesLoadingPage(false);
          setMessagesFirstLoad(false);
        });
    },
    [selectedChat?.externalId, messagesPageState]
  );

  useEffect(() => {
    if (!selectedChat?.externalId) {
      setMembers([]);
      setMessages([]);
      setMessagesFirstLoad(false);
      return;
    }

    fetchMessagesPage({ page: 0, totalPages: null });

    LazoChatConversationService.getChatMembers(selectedChat.externalId).then((r) => {
      r.onSuccess((c) => setMembers(c));
      r.notifyErrorIfPresent();
    });
  }, [selectedChat?.externalId]);

  function readMessages(chat: LazoChatConversationShell) {
    if (chat.externalId) {
      LazoChatConversationService.readConversation(chat.externalId).then((_) => {
        setChats((prev) =>
          prev.map((c) => {
            if (c.externalId === chat.externalId) {
              return { ...c, unreadMessagesCount: 0 };
            }
            return c;
          })
        );
      });
    }
  }

  function handleChatSelect(chat: LazoChatConversationShell) {
    setSelectedChat(chat);
    setShowMobileChatView(true);
    if (!chat) {
      // We consider it to be the creation of a new conversation
      return;
    }

    navigate(generatePath(LazoPaths.ONE_COMMUNICATIONS, { LazoChatTeamExternalId: chat.externalId }), { replace: true });
    setChatPreview(null);
    readMessages(chat);
  }

  async function createPrivateGroupChat(message: string, members: string[]) {
    return LazoChatConversationService.createPrivateGroupChat(members, null, currentTenantMembership.tenant.externalId, message).then(
      (r) => {
        r.onSuccess((c) => {
          const shellChat = mapToShell(c);
          setChats((prev) => [shellChat, ...prev]);
          handleChatSelect(shellChat);
        });
        r.notifyErrorIfPresent();

        return r;
      }
    );
  }

  function startNewChat(message: string) {
    if (newConversationMembers.length > 1) {
      createPrivateGroupChat(message, newConversationMembers);
    } else if (newConversationMembers.length === 1) {
      LazoChatConversationService.createPrivateChat(newConversationMembers[0], currentTenantMembership.tenant.externalId, message).then(
        (r) => {
          r.onSuccess((c) => {
            const shellChat = mapToShell(c);
            setChats((prev) => [shellChat, ...prev]);
            handleChatSelect(shellChat);
          });
          r.notifyErrorIfPresent();
        }
      );
    }
  }

  return (
    <ctx.Provider
      value={{
        chats,
        setChats,
        selectedChat,
        setSelectedChat,
        messages,
        setMessages,
        chatsSearchValue,
        setChatsSearchValue,
        chatsFirstLoad,
        setChatsFirstLoad,
        members,
        setMembers,
        chatsLoadingPage,
        fetchChatsPage,
        messagesFirstLoad,
        messagesLoadingPage,
        fetchMessagesPage,
        selectedActivityFilter,
        setSelectedActivityFilter,
        selectedDateFilter,
        setSelectedDateFilter,
        chatPreview,
        setChatPreview,
        handleChatSelect,
        newConversationMembers,
        setNewConversationMembers,
        startNewChat,
        createPrivateGroupChat,
        showMobileChatView,
        setShowMobileChatView,
      }}
    >
      {children}
    </ctx.Provider>
  );
}

export function useOneChatContext() {
  return useContext(ctx);
}
